/**
 * ============================================================================
 * KCB — TRIAL DAY FLEX BOOKING PROTECTION
 * File: bta-trial-day-flex.js
 *
 * CHANGE LOG
 *   Flex modal fix (Oct 31 2026): on phones the card was capped at 94vh with
 *     overflow hidden, so Confirm was cut off on shorter screens (iPhone SE/16,
 *     Galaxy A55). The card is now sized to the visible screen (visualViewport),
 *     the content scrolls, and Booking total + Confirm sit in a pinned footer
 *     that is always on screen.
 * ============================================================================
 *
 * PURPOSE
 *   Adds Flex Booking Protection to the Dog Daycare Trial Day product page
 *   (/products/dog-daycare-trial-day) only.
 *
 * SCOPE — this file does ONE thing:
 *   1. Intercepts /cart/add.js
 *   2. Shows the Flex Booking Protection modal (once per booking session)
 *   3. Injects the four Flex line properties that cart-kcb-enhancements.js reads
 *
 *   It does NOT inject drop-off / pick-up times, does NOT touch the calendar,
 *   and does NOT support multi-date selection. The trial day widget is a single
 *   date, single booking flow.
 *
 * CART INTEGRATION
 *   cart-kcb-enhancements.js already recognises this product via
 *   CONFIG.trialDayProductId (8226605367533) inside isBookingItem(). It performs
 *   the actual Flex line item add. This file only supplies the properties.
 *
 *   Properties injected:
 *     _flexProtection      'Yes' | 'No'
 *     _bookingSessionId    unique per booking session
 *     _flexFee             dollars, only when decision is 'Yes'
 *     Booking Protection   customer-visible status string
 *
 * PRICING
 *   Trial day has six variants ($65 / $80 / $105 / $125 / $145 / $190).
 *   Price is resolved from /products/dog-daycare-trial-day.js by variant ID —
 *   authoritative, matches final_line_price exactly, and survives both price
 *   changes and BTA restyling. DOM reading is a fallback only.
 *
 *   Fee is a FLAT 5.35% (the top tier), floor $1.50, rounded to $0.25.
 *     $65 → $3.50    $80 → $4.25    $105 → $5.50
 *     $125 → $6.75   $145 → $7.75   $190 → $10.25
 *
 *   These sit at or above the standard tiers used by cart-kcb-enhancements.js,
 *   so its shortfall fallback stays dormant (it only ever tops up, never refunds).
 *
 * Version 1.0 — initial build
 * ============================================================================
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  const ENABLE_LOGGING = false;   // ← set true for console debugging

  const PRODUCT_HANDLE   = 'dog-daycare-trial-day';
  const PRODUCT_JSON_URL = '/products/dog-daycare-trial-day.js';

  const FLEX_CONFIG = {
    // Reference only — cart-kcb-enhancements.js performs the actual cart add.
    // Listed here so all Flex variant IDs stay discoverable in one place.
    flexVariantIds: [
      49718668984557,  // Flex Booking Protection
      49718669246701,  // Flex Booking Protection 2
      49718669410541,  // Flex Booking Protection 3
      49718669508845,  // Flex Booking Protection 4
      49718669902061,  // Flex Booking Protection 5
    ],
    noProtectionVariantId: 49732858446061,

    // Trial day uses a FLAT top-tier rate rather than the sliding tiers used by
    // boarding and daycare. See header note.
    flatRate: 0.0535,
    feeFloor: 1.50,

    calculateFee(orderAmount) {
      const exact = Math.max(this.feeFloor, orderAmount * this.flatRate);
      return Math.round(exact * 4) / 4;   // round to nearest $0.25
    }
  };

  // ============================================================================
  // FLEX STATE — tracks the customer decision for this booking session
  // ============================================================================

  const flexState = {
    decision: null,   // 'yes' | 'no'
    shown: false,     // whether the modal has been shown this session
    sessionId: null,  // ties the booking line to its Flex cart item

    reset() {
      this.decision = null;
      this.shown = false;
      this.sessionId = null;
      log('flexState reset');
    }
  };

  // ============================================================================
  // LOGGING UTILITY
  // ============================================================================

  function log(...args) {
    if (ENABLE_LOGGING) {
      console.log('[KCB Trial Day Flex]', ...args);
    }
  }

  // ============================================================================
  // PAGE GATE
  // ============================================================================
  // theme.liquid already scopes this file to the trial day product, but guard
  // defensively so the fetch wrapper can never be installed elsewhere.

  if (window.location.pathname.indexOf(PRODUCT_HANDLE) === -1) {
    log('Not on the trial day page — script inactive');
    return;
  }

  // Capture the native fetch BEFORE wrapping it. Used for the product JSON
  // lookup so the wrapper can never recurse into itself.
  const originalFetch = window.fetch;

  // ============================================================================
  // VARIANT PRICE RESOLUTION
  // ============================================================================
  // Primary price source. Loads once, caches, and is prefetched on script load
  // so the lookup is already resolved by the time the customer clicks Book Now.

  let variantPriceMap = null;      // { variantId(string): priceInCents }
  let variantPricePromise = null;

  function loadVariantPrices() {
    if (variantPricePromise) return variantPricePromise;

    variantPricePromise = originalFetch(PRODUCT_JSON_URL, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then((res) => (res && res.ok) ? res.json() : null)
      .then((data) => {
        if (!data || !Array.isArray(data.variants)) {
          log('⚠ Product JSON returned no variants');
          return null;
        }
        const map = {};
        data.variants.forEach((v) => {
          if (v && v.id != null && v.price != null) {
            map[String(v.id)] = v.price;   // Shopify returns cents
          }
        });
        variantPriceMap = map;
        log('✓ Variant price map loaded:', map);
        return map;
      })
      .catch((err) => {
        log('⚠ Could not load product JSON:', err && err.message);
        return null;
      });

    return variantPricePromise;
  }

  // Warm the cache immediately.
  loadVariantPrices();

  // ============================================================================
  // DOM PRICE FALLBACK
  // ============================================================================
  // Used only if the variant lookup fails. The BTA widget renders its price
  // under a different class signature on each widget instance — boarding and
  // daycare use styled-components hashes (hXgXeP), trial day uses ml10 — so all
  // known selectors are tried before falling back to a leaf-node dollar scan.

  function getAmountFromWidgetDOM() {
    const iframe = document.querySelector('iframe[title="widget_xcomponent"]');
    if (!iframe) return null;

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow.document;
    } catch (e) {
      log('⚠ Widget iframe not accessible:', e && e.message);
      return null;
    }
    if (!doc || !doc.body) return null;

    let priceElement = doc.querySelector('span.sc-iHGNWf.sc-carFqZ.hXgXeP')
                    || doc.querySelector('span.hXgXeP')
                    || doc.querySelector('span.ml10');

    if (!priceElement) {
      const nodes = doc.querySelectorAll('span,div,p,td,strong,b');
      for (const node of nodes) {
        if (node.children.length === 0 && /\$\s*\d/.test(node.textContent)) {
          priceElement = node;
          break;
        }
      }
    }

    if (!priceElement) {
      log('⚠ getAmountFromWidgetDOM: no price element found');
      return null;
    }

    const raw = priceElement.textContent.trim().replace(/[^0-9.]/g, '');
    const amount = parseFloat(raw);
    if (isNaN(amount) || amount <= 0) {
      log('⚠ getAmountFromWidgetDOM: could not parse', priceElement.textContent);
      return null;
    }

    log(`✓ getAmountFromWidgetDOM: $${amount}`);
    return amount;
  }

  // ============================================================================
  // BOOKING AMOUNT
  // ============================================================================
  // Variant lookup first, DOM second, null last. Returning null is safe: the
  // modal simply omits the figures, and cart-kcb-enhancements.js recalculates
  // the expected fee from final_line_price and tops up any shortfall.

  async function getTrialDayAmount(formData) {
    const variantId = String(formData.get('id') || '');
    const qtyRaw = parseInt(formData.get('quantity') || '1', 10);
    const qty = (isNaN(qtyRaw) || qtyRaw < 1) ? 1 : qtyRaw;

    const map = variantPriceMap || await loadVariantPrices();

    if (map && map[variantId] != null) {
      const amount = (map[variantId] / 100) * qty;
      if (amount > 0) {
        log(`✓ Amount from variant ${variantId} x${qty}: $${amount.toFixed(2)}`);
        return amount;
      }
    }

    log(`⚠ Variant ${variantId} not in price map — falling back to DOM`);
    const domAmount = getAmountFromWidgetDOM();
    if (domAmount) return domAmount * qty;

    log('⚠ Booking amount could not be determined');
    return null;
  }

  // ============================================================================
  // FLEX BOOKING MODAL
  // ============================================================================
  // Copied verbatim from bta-mb-main.js (daycare) so wording, styling and the
  // two-step no-protection confirmation stay identical across all products.

  function showFlexModal(bookingAmount) {
    return new Promise((resolve) => {
      log('Showing Flex Booking modal');

      const flexFee    = bookingAmount ? FLEX_CONFIG.calculateFee(bookingAmount) : null;
      const refundBack = bookingAmount || null;
      const fmt        = (n) => n !== null ? '$' + n.toFixed(2) : '—';
      const feeLabel   = flexFee !== null ? fmt(flexFee) : '';

      const modalId = 'flex-booking-modal-' + Date.now();

      const totalPaid = bookingAmount && flexFee !== null ? bookingAmount + flexFee : null;
      const breakdownHTML = (bookingAmount && flexFee !== null) ? `
        <div style="
          background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;
          padding:11px 14px;margin:10px 0 14px;font-size:13px;
        ">
          <div style="font-weight:600;color:#374151;margin-bottom:7px;">If you cancel:</div>
          <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
            <span>Total paid (booking + protection)</span><span>${fmt(totalPaid)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
            <span>Protection cost (non-refundable)</span><span>− ${fmt(flexFee)}</span>
          </div>
          <div style="border-top:1px solid #e5e7eb;margin:7px 0;"></div>
          <div style="display:flex;justify-content:space-between;font-weight:700;color:#15803d;">
            <span>You receive back</span><span>${fmt(bookingAmount)}</span>
          </div>
        </div>` : '';

      const modalHTML = `
        <style>
          @keyframes kcbFlexFadeIn  { from{opacity:0} to{opacity:1} }
          @keyframes kcbFlexSlideUp { from{transform:translateY(30px);opacity:0} to{transform:translateY(0);opacity:1} }
          @keyframes kcbFlexFadeOut { from{opacity:1} to{opacity:0} }

          .kcb-flex-option {
            display:flex;align-items:flex-start;gap:13px;
            border:2px solid #e5e7eb;border-radius:10px;
            padding:14px 16px;cursor:pointer;
            transition:border-color 0.18s,background 0.18s;
            background:white;margin-bottom:10px;
          }
          .kcb-flex-option:last-of-type { margin-bottom:0; }
          .kcb-flex-option.selected-yes { border-color:#BD9B60;background:#fdfaf5; }
          .kcb-flex-option.selected-no  { border-color:#d1d5db;background:#f9fafb; }
          .kcb-flex-radio {
            width:20px;height:20px;border-radius:50%;
            border:2px solid #d1d5db;flex-shrink:0;margin-top:2px;
            display:flex;align-items:center;justify-content:center;
            transition:border-color 0.18s,background 0.18s;
          }
          .kcb-flex-option.selected-yes .kcb-flex-radio { border-color:#BD9B60;background:#BD9B60; }
          .kcb-flex-option.selected-yes .kcb-flex-radio::after {
            content:'';display:block;width:8px;height:8px;border-radius:50%;background:white;
          }
          .kcb-flex-option.selected-no .kcb-flex-radio { border-color:#9ca3af;background:#9ca3af; }
          .kcb-flex-option.selected-no .kcb-flex-radio::after {
            content:'';display:block;width:8px;height:8px;border-radius:50%;background:white;
          }
          #kcb-flex-confirm-btn {
            width:100%;padding:14px;border:none;border-radius:8px;
            background:#BD9B60;color:white;font-size:15px;font-weight:700;
            cursor:pointer;transition:background 0.18s;letter-spacing:0.01em;margin-top:18px;
          }
          #kcb-flex-confirm-btn:hover { background:#a37f4a; }
          #kcb-flex-learn-btn {
            background:none;border:none;padding:0;
            color:#BD9B60;font-size:13px;font-weight:600;
            cursor:pointer;text-decoration:underline;
            display:inline-flex;align-items:center;gap:4px;
          }
          @media(max-width:480px){
            #${modalId} .kcb-flex-inner {
              margin:0 auto !important;width:100% !important;
              /* v4.7.1: 94vh cap removed - height is now fitted to the visible screen in JS */
            }
          }
        </style>

        <div id="${modalId}" style="
          position:fixed;inset:0;z-index:9999999;
          background:rgba(0,0,0,0.6);
          display:flex;align-items:flex-start;justify-content:center;
          padding:16px 12px;box-sizing:border-box;
          animation:kcbFlexFadeIn 0.25s ease-out;
          overflow-y:auto;
        ">
          <div class="kcb-flex-inner" style="
            background:white;border-radius:14px;
            width:100%;max-width:480px;
            box-shadow:0 20px 60px rgba(0,0,0,0.25);
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
            animation:kcbFlexSlideUp 0.28s ease-out;
            display:flex;flex-direction:column;
            max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);
            overflow-x:hidden;overflow-y:auto;
          ">

            <div style="height:5px;background:#BD9B60;flex:0 0 auto;"></div>
            <!-- v4.7.1: scrolling middle - everything except the pinned footer -->
            <div class="kcb-flex-scroll" style="flex:1 1 auto;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;">

            <div style="padding:22px 24px 0;">
              <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px;">
                <div style="
                  width:38px;height:38px;border-radius:50%;
                  background:#fdf6e9;border:2px solid #BD9B60;
                  display:flex;align-items:center;justify-content:center;
                  font-size:18px;flex-shrink:0;
                ">🛡️</div>
                <div>
                  <div style="font-size:18px;font-weight:700;color:#1f2937;line-height:1.2;">
                    Booking Protection
                  </div>
                  <div style="font-size:13px;color:#6b7280;margin-top:2px;">
                    Life is unpredictable. Flex Booking keeps your plans protected.
                  </div>
                </div>
              </div>
            </div>

            <div style="padding:16px 24px 0;">

              <div class="kcb-flex-option selected-yes" id="kcb-opt-yes">
                <div class="kcb-flex-radio"></div>
                <div style="flex:1;">
                  <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:6px;">
                    <div style="font-size:15px;font-weight:700;color:#1f2937;">Protect my booking</div>
                    <div style="
                      background:#fdf6e9;border:1px solid #BD9B60;
                      color:#92400e;font-size:13px;font-weight:700;
                      padding:3px 10px;border-radius:20px;white-space:nowrap;
                    ">${feeLabel}</div>
                  </div>
                  <div style="font-size:13px;color:#15803d;font-weight:600;margin-top:4px;">
                    ✓ Refundable &amp; Transferable
                  </div>
                  <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                    Cancel or transfer up to 6pm the day before your booking — fully refundable &amp; transferable.
                  </div>
                </div>
              </div>

              <div class="kcb-flex-option" id="kcb-opt-no">
                <div class="kcb-flex-radio"></div>
                <div style="flex:1;">
                  <div style="font-size:15px;font-weight:700;color:#374151;">No protection</div>
                  <div style="font-size:13px;color:#dc2626;font-weight:600;margin-top:4px;">
                    ✕ Non-Refundable &amp; Non-Transferable
                  </div>
                  <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                    I accept this booking is final and cannot be cancelled or transferred.
                  </div>
                </div>
              </div>


              <div style="margin-top:14px;">
                <button id="kcb-flex-learn-btn">▸ Conditions</button>
              </div>

              <div id="kcb-flex-learn-content" style="display:none;margin-top:12px;">
                <div style="
                  background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;
                  padding:16px;font-size:13px;color:#374151;line-height:1.65;
                ">
                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Cancellations</div>
                  <p style="margin:0 0 8px;">
                    Submit a cancellation request by 6pm the day before your booking and receive a full refund of your <strong>booking amount</strong>. The cost of Flex Protection is non-refundable.
                  </p>
                  ${breakdownHTML}
                  <p style="margin:0 0 14px;color:#6b7280;">
                    Cancellations must be requested by 6pm the day before your booking. Requests received
                    after 6pm the day before — or on, or after, the day of your booking — cannot be cancelled or
                    transferred, and that day will be charged in full. Each booked date is treated
                    independently; cancelling one date does not affect your other booked dates.
                  </p>

                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Transfers</div>
                  <p style="margin:0 0 8px;">
                    Transfer requests must be submitted by 6pm the day before the booking you wish to
                    transfer. Requests received after 6pm the day before — or on, or after, the day of your booking —
                    cannot be transferred or cancelled, and that day will be charged in full. Each booked
                    date can be transferred independently, subject to availability, at no additional charge.
                  </p>

                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Please note</div>
                  <p style="margin:0 0 8px;color:#6b7280;">
                    No-show bookings are not eligible for a refund or transfer regardless of Flex Booking Protection status.
                  </p>
                  <p style="margin:0 0 8px;color:#6b7280;">
                    The cost of Flex Booking Protection is non-refundable in all cases.
                    All refund and transfer requests must be submitted by email to
                    <a href="mailto:info@kennelclubboarding.ca" style="color:#BD9B60;font-weight:600;">
                      info@kennelclubboarding.ca
                    </a>
                  </p>
                  <p style="margin:0;color:#6b7280;">
                    <a href="https://kennelclubboarding.ca/policies/refund-policy"
                       target="_blank"
                       style="color:#BD9B60;font-weight:600;">
                      View full Cancellation &amp; Refund Policy →
                    </a>
                  </p>
                </div>
              </div>

            </div>

            </div><!-- /kcb-flex-scroll -->
            <!-- v4.7.1: pinned footer - booking total + Confirm always on screen -->
            <div class="kcb-flex-footer" style="flex:0 0 auto;padding:12px 24px 16px;background:white;border-top:1px solid #f3f4f6;box-shadow:0 -6px 14px rgba(0,0,0,0.05);">
              ${bookingAmount && flexFee !== null ? `
              <div style="
                display:flex;justify-content:space-between;align-items:center;
                padding:0 0 10px;margin-top:0;
                border-top:none;
              ">
                <span style="font-size:13px;color:#374151;">Booking total</span>
                <div id="kcb-flex-total-display" style="font-size:13px;font-weight:700;color:#1f2937;">${fmt(bookingAmount + flexFee)}</div>
              </div>
              ` : ''}
              <button id="kcb-flex-confirm-btn">Confirm</button>
            </div>

          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = modalHTML;
      document.body.appendChild(container);
      document.body.style.overflow = 'hidden';

      // v4.7.1: size the card to the visible screen (visualViewport excludes the
      // browser toolbars) so the pinned Confirm footer is always on screen
      const kcbFlexFit = () => {
        try {
          const fitInner = container.querySelector('.kcb-flex-inner');
          if (!fitInner || !fitInner.isConnected) {
            if (window.visualViewport) window.visualViewport.removeEventListener('resize', kcbFlexFit);
            window.removeEventListener('resize', kcbFlexFit);
            return;
          }
          const visibleH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
          fitInner.style.maxHeight = Math.max(300, Math.floor(visibleH - 32)) + 'px';
        } catch (fitErr) { /* never block the modal */ }
      };
      kcbFlexFit();
      if (window.visualViewport) window.visualViewport.addEventListener('resize', kcbFlexFit);
      window.addEventListener('resize', kcbFlexFit);

      let currentDecision = 'yes';

      const optYes = document.getElementById('kcb-opt-yes');
      const optNo  = document.getElementById('kcb-opt-no');

      const setSelection = (decision) => {
        currentDecision = decision;
        if (decision === 'yes') {
          optYes.classList.add('selected-yes');
          optYes.classList.remove('selected-no');
          optNo.classList.remove('selected-yes', 'selected-no');
        } else {
          optNo.classList.add('selected-no');
          optNo.classList.remove('selected-yes');
          optYes.classList.remove('selected-yes', 'selected-no');
        }
        const totalDisplay = document.getElementById('kcb-flex-total-display');
        if (totalDisplay && flexFee !== null) {
          totalDisplay.textContent = decision === 'yes' ? fmt(bookingAmount + flexFee) : fmt(bookingAmount);
        }
      };

      optYes.addEventListener('click', () => setSelection('yes'));
      optNo.addEventListener('click',  () => setSelection('no'));

      const learnBtn     = document.getElementById('kcb-flex-learn-btn');
      const learnContent = document.getElementById('kcb-flex-learn-content');
      learnBtn.addEventListener('click', () => {
        const open = learnContent.style.display !== 'none';
        learnContent.style.display = open ? 'none' : 'block';
        learnBtn.innerHTML = open ? '▸ Conditions' : '▾ Hide conditions';
      });

      const closeModal = (decision) => {
        const overlay = document.getElementById(modalId);
        if (overlay) overlay.style.animation = 'kcbFlexFadeOut 0.2s ease-out';
        setTimeout(() => {
          container.remove();
          document.body.style.overflow = 'auto';
          log(`✓ Flex modal confirmed — decision: ${decision}`);
          setTimeout(() => resolve(decision), 50);
        }, 200);
      };

      const showSecondConfirmation = () => {
        const inner = container.querySelector('.kcb-flex-inner');
        if (!inner) { closeModal('no'); return; }

        inner.innerHTML = `
          <div style="height:5px;background:#e5e7eb;border-radius:14px 14px 0 0;"></div>
          <div style="padding:28px 24px 24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
            <div style="text-align:center;margin-bottom:20px;">
              <div style="font-size:36px;margin-bottom:10px;">⚠️</div>
              <div style="font-size:17px;font-weight:700;color:#1f2937;margin-bottom:8px;">
                Just to confirm
              </div>
              <div style="
                font-size:14px;color:#374151;line-height:1.6;
                background:#fef2f2;border:1px solid #fecaca;
                border-radius:8px;padding:14px 16px;text-align:left;
              ">
                This booking will be <strong>non-refundable and non-transferable</strong>.
                No cancellations, refunds, or date changes will be possible after checkout.
              </div>
            </div>
            <div style="display:flex;flex-direction:column;gap:10px;margin-top:4px;">
              <button id="kcb-flex-goback-btn" style="
                width:100%;padding:14px;border-radius:8px;
                border:none;background:#BD9B60;
                color:white;font-size:15px;font-weight:700;
                cursor:pointer;transition:background 0.18s;
              ">Yes — protect my booking for ${feeLabel}</button>
              <button id="kcb-flex-confirm2-btn" style="
                width:100%;padding:12px;border-radius:8px;
                border:1.5px solid #d1d5db;background:white;
                color:#6b7280;font-size:13px;font-weight:500;
                cursor:pointer;transition:all 0.18s;line-height:1.3;
              ">No thanks — I accept this booking is non-refundable and non-transferable</button>
            </div>
          </div>
        `;

        document.getElementById('kcb-flex-goback-btn').addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          closeModal('yes');
        });

        document.getElementById('kcb-flex-confirm2-btn').addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          closeModal('no');
        });
      };

      document.getElementById('kcb-flex-confirm-btn').addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
        if (currentDecision === 'no') {
          showSecondConfirmation();
        } else {
          closeModal('yes');
        }
      });
    });
  }
  // ============================================================================
  // IFRAME REBUILD WATCHER
  // ============================================================================
  // Mirrors the boarding bundle's behaviour: if BTA tears down and rebuilds the
  // widget, the previous booking session is over, so the modal must show again
  // and a fresh sessionId must be issued for the next booking.

  let lastWidgetDoc = null;

  setInterval(function() {
    const iframe = document.querySelector('iframe[title="widget_xcomponent"]');
    if (!iframe) return;

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow.document;
    } catch (e) {
      return;
    }
    if (!doc || !doc.body) return;

    if (lastWidgetDoc === null) {
      lastWidgetDoc = doc;
      return;
    }

    if (doc !== lastWidgetDoc) {
      lastWidgetDoc = doc;
      if (flexState.shown) {
        log('Widget rebuilt — resetting Flex state for a new booking session');
        flexState.reset();
      }
    }
  }, 1000);

  // ============================================================================
  // FETCH INTERCEPTOR
  // ============================================================================
  // The trial day widget posts to /cart/add.js with:
  //   id, quantity, properties[Start], properties[_start_iso8601],
  //   properties[Units], properties[_addons_uuid]
  //
  // Note there is NO properties[Date] — that is daycare only. properties[Start]
  // is the trial day signal.

  window.fetch = async function(url, options) {
    const urlStr = (typeof url === 'string')
      ? url
      : (url && url.url) ? url.url : '';

    if (urlStr.indexOf('/cart/add') > -1 && options && options.body instanceof FormData) {
      const formData = options.body;

      let startValue = null;
      for (const [key, value] of formData.entries()) {
        if (key === 'properties[Start]') {
          startValue = value;
          break;
        }
      }

      if (startValue) {
        log('📦 Intercepting trial day cart add — Start:', startValue);

        const bookingAmount = await getTrialDayAmount(formData);

        // ── FLEX BOOKING MODAL ─────────────────────────────────────────────
        // Shown once per booking session. The fetch is held open until the
        // customer decides, so BTA cannot navigate away underneath the modal.
        if (!flexState.shown) {
          log('Flex: showing modal before cart add');
          const decision = await showFlexModal(bookingAmount);
          flexState.decision = decision;
          flexState.shown = true;
          log(`Flex: decision="${decision}"`);
        }

        // Private property — read by cart-kcb-enhancements.js to add or
        // suppress the Flex cart item.
        const flexValue = flexState.decision === 'yes' ? 'Yes' : 'No';
        formData.append('properties[_flexProtection]', flexValue);

        // Unique booking session ID — ties this booking to its Flex cart item.
        if (!flexState.sessionId) {
          flexState.sessionId = 'kcb_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
        }
        formData.append('properties[_bookingSessionId]', flexState.sessionId);
        log(`  _bookingSessionId: ${flexState.sessionId}`);

        // Private property — calculated fee in dollars. The cart uses this to
        // set the Flex variant quantity (qty = fee x 100).
        if (flexState.decision === 'yes' && bookingAmount) {
          const calculatedFee = FLEX_CONFIG.calculateFee(bookingAmount);
          formData.append('properties[_flexFee]', calculatedFee.toFixed(2));
          log(`  _flexFee: ${calculatedFee.toFixed(2)}`);
        }

        // Public property — visible to the customer in the confirmation email
        // and in order history.
        const flexPublicValue = flexState.decision === 'yes'
          ? '✓ Protected — Refundable & Transferable'
          : '✕ No Protection — Non-Refundable & Non-Transferable';
        formData.append('properties[Booking Protection]', flexPublicValue);

        log(`  _flexProtection: ${flexValue}`);
        log(`  Booking Protection: ${flexPublicValue}`);
        // ── END FLEX BOOKING MODAL ─────────────────────────────────────────
      }
    }

    return originalFetch.apply(this, arguments);
  };

  log('✓ Trial day Flex interceptor installed');

})();